import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { v4 } from 'uuid';
import { total, expire, calendar } from 'utils/icons';

import ModalPeriod from 'components/ModalPeriod';

import { Tier } from 'services/api/tier/types';
import { Member } from 'services/api/member/types';

import { formatDate } from 'helpers';

import AccountDataCard from '../AccountDataCard';
import { VbzIcon, AirplaneIcon, VbzIconExclusive } from '../icons';
import AccountDataButton from '../AccountDataButton';

// const formatThousands = (text?: string) => { return text?.replace(/([\d]+)([\d]{3})/, '$1.$2') ?? ''; };

const renderTarget = (target: number) => {
  if (target === 1000000) {
    return <div>(R$1 milhão)</div>;
  }

  if (target > 1000000) {
    return <div>{`(R$${target / 1000000} milhões)`}</div>;
  }

  if (target >= 1000) {
    return <div>{`(R$${target / 1000} mil)`}</div>;
  }

  return null;
};

export interface AccountDataSectionsProps {
  tierData: Tier;
  userData: Member;
  isClubSmls: boolean;
}

const AccountDataSections: FC<AccountDataSectionsProps> = ({
  tierData: { tierList, userInfo },
  userData,
  isClubSmls,
}) => {
  const periodDateFrom = `01/01/${new Date().getFullYear()}`;
  const periodDateTo = `31/12/${new Date().getFullYear()}`;

  const category = tierList.find(({ active }) => active === 'Y');
  const itemSize = 19.6;
  const marginSize = 0.5;

  const totalPoints = +(userInfo.upgradeInfo.totalMilesClub ?? 0);

  const [progress, setProgress] = useState(0);
  const [current, setCurrent] = useState(0);
  const [currentCategoryPercent, setCurrentCategoryPercent] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>();

  const categories = useMemo(() => {
    return [
      { id: 'voebiz', name: 'VoeBiz', target: 0 },
      { id: 'essencial', name: 'Essencial', target: 100000 },
      { id: 'prime', name: 'Prime', target: 350000 },
      { id: 'super-prime', name: 'Super Prime', target: 1000000 },
      { id: 'exclusive', name: 'Exclusive', target: 2000000 },
    ];
  }, []);

  const handleSetProgress = useCallback(() => {
    const currentCategory = categories[current];
    const nextCategory = categories[current + 1];

    if (currentCategory && nextCategory) {
      const range = nextCategory.target - currentCategory.target;

      const categoryPercent = (totalPoints - currentCategory.target) / range;

      setCurrentCategoryPercent(categoryPercent * 100);

      const newProgress = current * itemSize + current * marginSize + categoryPercent * itemSize;

      setProgress(newProgress);
    }
  }, [categories, current, totalPoints]);

  const handleModalToggle = () => setIsModalOpen((prev) => !prev);

  const handleNavigateToBenefits = () => {
    document.getElementById('category-benefits')?.scrollIntoView();
  };

  useEffect(() => {
    for (let i = categories.length - 1; i >= 0; i -= 1) {
      if (totalPoints >= categories[i].target) {
        setCurrent(i);
        break;
      }
    }
  }, [categories, totalPoints]);

  useEffect(() => {
    handleSetProgress();
  }, [handleSetProgress]);

  if (!category) return null;

  const isExclusive = category.name === 'Exclusive';

  return (
    <div className={`account-data-sections ${tierList[0].name.toLowerCase().replace(' ', '-')}`}>
      <ModalPeriod toggle={handleModalToggle} isOpen={isModalOpen} />
      <AccountDataCard className="benefits">
        <div className="account-data-card__benefits__icon">{isExclusive ? <VbzIconExclusive /> : <VbzIcon />}</div>
        <div className="account-data-card__benefits__title">
          <div className="small">Você está na</div>
          <p className="category-name">
            Categoria {tierList[0].name}
            {isClubSmls && !isExclusive && (
              <>
                <span className="plus">+</span>
                <span className="club-badge">
                  Clube{' '}
                  <span className="desktop" style={{ font: 'inherit' }}>
                    Smiles para Empresas
                  </span>
                </span>
              </>
            )}
          </p>

          <div className="expire">Renova em {formatDate(userInfo?.dateUpgradeLimit)}</div>
        </div>
        <div className="account-data-card__benefits__more">
          <AccountDataButton onClick={handleNavigateToBenefits}>Confira seus benefícios</AccountDataButton>
        </div>
      </AccountDataCard>
      {!isExclusive && (
        <>
          <AccountDataCard className="expires">
            <div>
              <div className="top">
                <div className="text">Total de pontos:</div>
              </div>
              <div className="bottom">
                <div className="icon">
                  <img src={total} alt="" />
                </div>
                <div className="text big">{Intl.NumberFormat('pt-BR').format(Number(userData.milesBalance))}</div>
              </div>
            </div>
            <div>
              <div className="top">
                <div className="text">Pontos a expirar:</div>
              </div>
              <div className="bottom">
                <div className="icon">
                  <img src={expire} alt="" />
                </div>
                <div className="text">{userData.milesToExpire}</div>
              </div>
            </div>
            <div>
              <div className="top">
                <div className="text">Expiram em:</div>
              </div>
              <div className="bottom">
                <div className="icon">
                  <img src={calendar} alt="" />
                </div>
                <div className="text">{formatDate(userData?.milesNextExpirationDate.slice(0, 10))}</div>
              </div>
            </div>
          </AccountDataCard>
          <AccountDataCard className="total">
            <div className="account-data-card__total__title">Valor total em passagens GOL:</div>
            <div className="account-data-card__total__button">
              <AccountDataButton onClick={handleModalToggle}>Período de acúmulo</AccountDataButton>
            </div>
            <div className="account-data-card__total__total">
              R${Intl.NumberFormat('pt-BR').format(Number(totalPoints))}
            </div>
            <div className="account-data-card__total__from-to">
              <div>{periodDateFrom} à</div>
              <div>{periodDateTo}</div>
            </div>
            <div className="account-data-card__total__timeline">
              <div className="line" style={{ width: `${Math.min(progress, 80)}%` }}>
                <AirplaneIcon />
              </div>
            </div>
            <div className="account-data-card__total__bottom">
              {categories.map(({ id, name, target }, index) => {
                let fillWidth;

                let className = `category ${id}`;

                if (index < current) {
                  className += ' current--prev';
                }

                if (index === current) {
                  className += ' current';
                  fillWidth = Math.min(currentCategoryPercent, 100);
                }

                return (
                  <div className={className} key={v4()}>
                    <div className="line">
                      <div className="fill" style={{ width: `${fillWidth}%` }} />
                    </div>
                    <div>{name}</div>
                    {renderTarget(target)}
                  </div>
                );
              })}
            </div>
          </AccountDataCard>
        </>
      )}
    </div>
  );
};

export default AccountDataSections;
